import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Mail, RefreshCw, Inbox, CheckCircle2, Archive, Trash2,
  Clock, ChevronRight, Bug, Tag, Lightbulb, Crown, ShieldCheck, MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUBJECT_META = {
  bug: { label: "Bug", icon: Bug, color: "bg-rose-100 text-rose-700" },
  price: { label: "Prix", icon: Tag, color: "bg-amber-100 text-amber-700" },
  feature: { label: "Idée", icon: Lightbulb, color: "bg-violet-100 text-violet-700" },
  premium: { label: "Premium", icon: Crown, color: "bg-yellow-100 text-yellow-700" },
  rgpd: { label: "RGPD", icon: ShieldCheck, color: "bg-blue-100 text-blue-700" },
  other: { label: "Autre", icon: MessageSquare, color: "bg-slate-100 text-slate-700" },
};

const STATUS_META = {
  new: { label: "Nouveau", color: "bg-rose-500 text-white" },
  in_progress: { label: "En cours", color: "bg-amber-500 text-white" },
  resolved: { label: "Résolu", color: "bg-emerald-500 text-white" },
  archived: { label: "Archivé", color: "bg-slate-400 text-white" },
};

const FILTERS = [
  { key: "new", label: "Nouveaux", icon: Inbox },
  { key: "in_progress", label: "En cours", icon: Clock },
  { key: "resolved", label: "Résolus", icon: CheckCircle2 },
  { key: "archived", label: "Archivés", icon: Archive },
  { key: "all", label: "Tous", icon: Mail },
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminContact() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, resolved: 0, archived: 0, total: 0 });
  const [filter, setFilter] = useState("new");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const params = status && status !== "all" ? { status } : {};
      const { data } = await api.get("/admin/contact/messages", { params });
      setItems(data.items);
      setCounts(data.counts);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      await api.patch(`/admin/contact/messages/${id}`, { status });
      toast.success(`Statut mis à jour : ${STATUS_META[status].label}`);
      // Update local state
      setItems((arr) => arr.map((m) => (m.id === id ? { ...m, status } : m)));
      if (selected?.id === id) setSelected({ ...selected, status });
      // Refresh counts
      const params = filter && filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/admin/contact/messages", { params });
      setCounts(data.counts);
      // If item left the filter, drop it from list
      if (filter !== "all" && status !== filter) {
        setItems((arr) => arr.filter((m) => m.id !== id));
        if (selected?.id === id) setSelected(null);
      }
    } catch {
      toast.error("Impossible de mettre à jour");
    } finally {
      setUpdating(false);
    }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Supprimer définitivement ce message ?")) return;
    try {
      await api.delete(`/admin/contact/messages/${id}`);
      setItems((arr) => arr.filter((m) => m.id !== id));
      if (selected?.id === id) setSelected(null);
      setCounts((c) => ({ ...c, total: Math.max(0, c.total - 1) }));
      toast.success("Message supprimé");
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-contact-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-10 pb-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">// ADMIN · MESSAGES</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
              Boîte de réception
            </h1>
            <p className="text-sm text-slate-500 mt-1">{counts.total} message{counts.total > 1 ? "s" : ""} au total</p>
          </div>
          <button
            onClick={() => load(filter)}
            data-testid="admin-contact-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-700 hover:border-slate-400 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-2">
          {FILTERS.map(({ key, label, icon: Icon }) => {
            const isActive = filter === key;
            const c = key === "all" ? counts.total : counts[key] || 0;
            return (
              <button
                key={key}
                onClick={() => { setFilter(key); setSelected(null); }}
                data-testid={`admin-contact-tab-${key}`}
                className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl transition ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20" : "bg-slate-100"}`}>
                  {c}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          {/* Message list */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {loading && items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">Chargement…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400" data-testid="admin-contact-empty">
                <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                Aucun message {filter !== "all" && `dans « ${FILTERS.find(f => f.key === filter)?.label} »`}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                {items.map((m) => {
                  const meta = SUBJECT_META[m.subject] || SUBJECT_META.other;
                  const Icon = meta.icon;
                  const isSelected = selected?.id === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => setSelected(m)}
                        data-testid={`admin-contact-item-${m.id}`}
                        className={`w-full text-left p-4 hover:bg-slate-50 transition flex items-start gap-3 ${
                          isSelected ? "bg-slate-50" : ""
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-slate-900 truncate">
                              {m.name || m.email.split("@")[0]}
                            </span>
                            {m.status === "new" && (
                              <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{m.email}</p>
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{m.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-[10px] text-slate-400">{formatDate(m.created_at)}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_META[m.status]?.color || ""}`}>
                              {STATUS_META[m.status]?.label || m.status}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-2" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Selected message detail */}
          <div className="bg-white rounded-2xl border border-slate-200 min-h-[400px]">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Mail className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Sélectionnez un message à gauche pour le lire</p>
              </div>
            ) : (
              <div className="p-6 lg:p-8" data-testid="admin-contact-detail">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-md ${SUBJECT_META[selected.subject]?.color}`}>
                        {SUBJECT_META[selected.subject]?.label}
                      </span>
                      <span className={`text-[10px] px-2 py-1 rounded-md font-semibold ${STATUS_META[selected.status]?.color}`}>
                        {STATUS_META[selected.status]?.label}
                      </span>
                    </div>
                    <h2 className="font-heading text-xl font-extrabold text-slate-900">
                      {selected.name || "Anonyme"}
                    </h2>
                    <a
                      href={`mailto:${selected.email}?subject=Re: votre message sur Cartonomie`}
                      data-testid="admin-contact-reply-link"
                      className="text-sm text-[#E11D48] font-semibold hover:underline"
                    >
                      {selected.email}
                    </a>
                    <p className="text-[11px] text-slate-400 mt-1">{formatDate(selected.created_at)}</p>
                  </div>
                  <button
                    onClick={() => deleteMsg(selected.id)}
                    data-testid="admin-contact-delete-btn"
                    className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="prose-sm max-w-none">
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Changer le statut</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_META).map(([key, meta]) => (
                      <button
                        key={key}
                        disabled={updating || selected.status === key}
                        onClick={() => updateStatus(selected.id, key)}
                        data-testid={`admin-contact-status-${key}`}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition ${
                          selected.status === key
                            ? `${meta.color} opacity-60 cursor-not-allowed`
                            : "border border-slate-200 text-slate-700 hover:border-slate-400"
                        }`}
                      >
                        {meta.label}
                      </button>
                    ))}
                  </div>
                  <a
                    href={`mailto:${selected.email}?subject=Re: votre message sur Cartonomie&body=Bonjour ${selected.name || ""},%0A%0AMerci pour votre message. `}
                    data-testid="admin-contact-mailto"
                    className="mt-4 inline-flex items-center gap-2 btn-primary text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    Répondre par e-mail
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
